// The record History tab's model (BP-04, GUI View 1210). This file is pure. It
// turns a page of GET /v6/users/history/ rows into day groups of display
// lines, and RecordHistoryTab.tsx renders them. Every History tab goes through
// it, so the three rules below hold on all of them at once (H244):
//
// 1. WHEN — a naive `created` is UTC, and the tab shows it in the TENANT's
//    zone, shifted once.
//    `global_history_tracker.created` is `Column(DateTime, default=func.now())`
//    (app/history/models.py). The row is written by the database clock, and
//    the Cloud SQL instance's clock is UTC. Measured on aeronet-shared-db
//    2026-09-29: @@global.time_zone = SYSTEM, @@system_time_zone = UTC,
//    NOW() = UTC_TIMESTAMP(). Pydantic serialises that value naive
//    ("2026-09-28T19:03:32", with no Z). `new Date()` then reads it as the
//    VIEWER's local time, which is wrong by the viewer's offset.
//    - Before H221, getTimeFromISOString took the offset off by hand, and
//      toLocaleTimeString applied it again. For an NZ viewer that happened to
//      turn UTC into NZ time for the Time column. The Date column was still
//      read as NZ, so the Time and the Date disagreed.
//    - On mustang/flights/3455 (Aaron, 2026-09-30), rows written at 10:34Z
//      and 19:03Z on 28-Sep read "23:34 Created" BELOW "08:03 Updated", both
//      under 28-Sep. The order was right: -id, and the update came after the
//      create. The times were not.
//    - The tenant is in America/Toronto (`global_timezone`), so the true
//      times are 06:34 and 15:03 on 28-Sep.
//    An explicit Z or ±hh:mm offset is honoured as sent. The zone is the
//    tenant's `global_timezone` (served as `timezone`, utils/calendarDate.ts
//    tenantTimeZone), then the browser's own zone.
//
// 2. ORDER — newest first, and stable within a day. The read is sort=-id.
//    The page is then ordered by instant, newest first, with id breaking a
//    tie, so two entries in the same second never swap between renders.
//    Grouping keys on the TENANT-local calendar day, never on the label.
//
// 3. WHAT CHANGED — one line per changed field: Field · Old → New.
//    `previous_value` is served since Aeronet.app 67fddd59 (2026-08-29,
//    app/users/schemas.py GlobalHistoryFieldDetail). Upstream prod
//    (gitlab/develop, e.g. src/freight/FreightHistory.tsx) prints
//    "<Field> updated to <new>": no old value, and a status id shown through
//    the module's status list. The old value is kept here, and prod's
//    value-label mapping is a supported option (`formatValue`).
//    - A "Created…" entry carries every initial field. It reads as one line,
//      "record created", as it does in prod.
//    - Audit bookkeeping (created, modified, created_by, modified_by) is
//      never a line.

import type { HistoryFieldDetail, HistoryRow } from "./types";

/** BP-04: the app-wide history page size. */
export const HISTORY_TAB_PAGE_SIZE = 5;

/** Bookkeeping columns every save touches. They are never a "change". */
export const HISTORY_IGNORED_FIELDS = [
  "created",
  "modified",
  "created_by",
  "modified_by",
];

/** The browser's own IANA zone, or `undefined` if it will not say. */
function browserTimeZone(): string | undefined {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
  } catch {
    return undefined;
  }
}

const HAS_ZONE = /(Z|[+-]\d{2}:?\d{2})$/i;

/**
 * The instant a history `created` names. A naive string ("2026-09-28T19:03:32"
 * or "2026-09-28 19:03:32") is the database's UTC clock (see header rule 1);
 * one with Z or an offset is taken as sent. `null` for empty, the MySQL zero
 * date, and anything unreadable.
 */
export function parseHistoryInstant(
  created: string | null | undefined
): Date | null {
  if (!created) return null;
  const s = created.trim();
  if (!s || s.startsWith("0000-00-00")) return null;
  const iso = s.includes("T") ? s : s.replace(" ", "T");
  const d = new Date(HAS_ZONE.test(iso) ? iso : `${iso}Z`);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** `{ day: "YYYY-MM-DD", time: "HH:mm:ss" }` of an instant, on the clock of
 *  `timeZone` (falling back to the browser's zone for an unknown name). The
 *  output is built from parts, so the runner's locale cannot move it. */
export function zonedDayAndTime(
  instant: Date,
  timeZone?: string | null
): { day: string; time: string } {
  for (const tz of [(timeZone ?? "").trim(), browserTimeZone()]) {
    if (!tz) continue;
    try {
      const parts = new Intl.DateTimeFormat("en-CA", {
        timeZone: tz,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hourCycle: "h23",
      }).formatToParts(instant);
      const get = (t: string) => parts.find((p) => p.type === t)?.value ?? "";
      const hour = get("hour") === "24" ? "00" : get("hour");
      return {
        day: `${get("year")}-${get("month")}-${get("day")}`,
        time: `${hour}:${get("minute")}:${get("second")}`,
      };
    } catch {
      // An unknown zone name: try the next one.
    }
  }
  const p = (n: number) => String(n).padStart(2, "0");
  return {
    day: `${instant.getFullYear()}-${p(instant.getMonth() + 1)}-${p(
      instant.getDate()
    )}`,
    time: `${p(instant.getHours())}:${p(instant.getMinutes())}:${p(
      instant.getSeconds()
    )}`,
  };
}

/** Prod's field label: `status_id` → "Status id". A module can override a
 *  field through `fieldLabels`. */
export function defaultFieldLabel(field: string): string {
  const s = field.replace(/_/g, " ").trim();
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** One rendered line: a field that changed, or the entry itself (no field). */
export interface HistoryLine {
  field: string | null;
  label: string;
  oldValue: string;
  newValue: string;
}

export interface HistoryEntryView {
  row: HistoryRow;
  /** "HH:mm:ss" on the tenant's clock, or "" when `created` is unreadable. */
  time: string;
  user: string;
  action: string;
  /** "Created…" entries: one line that reads "record created". */
  isCreate: boolean;
  lines: HistoryLine[];
}

export interface HistoryDayView {
  /** Tenant-local "YYYY-MM-DD", or "" for entries with no readable time. */
  day: string;
  entries: HistoryEntryView[];
}

export interface HistoryModelOptions {
  /** The tenant's IANA zone (`global_timezone`); the browser's when absent. */
  timeZone?: string | null;
  /** Per-field label overrides, e.g. `{ status_id: "Status" }`. */
  fieldLabels?: Record<string, string>;
  /** Value display, e.g. a status id through the module's status list (prod
   *  does this for `status_id`). Return the raw value to leave it alone. */
  formatValue?: (field: string, value: string) => string;
  /** Extra fields to leave out, on top of HISTORY_IGNORED_FIELDS. */
  ignoredFields?: string[];
}

const text = (v: string | null | undefined) => (v == null ? "" : String(v));

/** Group a page of history rows into tenant-local days, newest first. */
export function buildHistoryDays(
  rows: HistoryRow[],
  opts: HistoryModelOptions = {}
): HistoryDayView[] {
  const ignored = new Set([
    ...HISTORY_IGNORED_FIELDS,
    ...(opts.ignoredFields ?? []),
  ]);
  const label = (f: string) => opts.fieldLabels?.[f] ?? defaultFieldLabel(f);
  const value = (f: string, v: string | null | undefined) => {
    const raw = text(v);
    return raw !== "" && opts.formatValue ? opts.formatValue(f, raw) : raw;
  };

  const timed = rows.map((row) => ({
    row,
    at: parseHistoryInstant(row.created),
  }));
  // Newest first by instant; id breaks a tie (and orders undated rows).
  timed.sort((a, b) => {
    const ta = a.at ? a.at.getTime() : -Infinity;
    const tb = b.at ? b.at.getTime() : -Infinity;
    if (ta !== tb) return tb - ta;
    return b.row.id - a.row.id;
  });

  const days: HistoryDayView[] = [];
  for (const { row, at } of timed) {
    const { day, time } = at
      ? zonedDayAndTime(at, opts.timeZone)
      : { day: "", time: "" };
    const action = text(row.description);
    const isCreate = /^created\b/i.test(action.trim());
    const changed = (row.details ?? []).filter(
      (d): d is HistoryFieldDetail & { field_name: string } =>
        !!d.field_name && !ignored.has(d.field_name)
    );
    const lines: HistoryLine[] =
      isCreate || changed.length === 0
        ? [{ field: null, label: "", oldValue: "", newValue: "" }]
        : changed.map((d) => ({
            field: d.field_name,
            label: label(d.field_name),
            oldValue: value(d.field_name, d.previous_value),
            newValue: value(d.field_name, d.current_value),
          }));
    const entry: HistoryEntryView = {
      row,
      time,
      user: text(row.user?.name),
      action,
      isCreate,
      lines,
    };
    const last = days[days.length - 1];
    if (last && last.day === day) last.entries.push(entry);
    else days.push({ day, entries: [entry] });
  }
  return days;
}

/**
 * `formatValue` from a module's config lists, keyed by the field they label:
 * `valueLabelsFrom({ status_id: config?.status })`. Prod shows a `status_id`
 * through the status list like this (gitlab/develop FreightHistory
 * formatDetailsSummary). A list may be an id → label map (`{ "2": "Closed" }`),
 * or an array of `{ id|value, name|label }`. A value that is not in the list
 * is shown raw, never dropped.
 */
export function valueLabelsFrom(
  lists: Record<string, unknown>
): (field: string, value: string) => string {
  const maps: Record<string, Record<string, string>> = {};
  for (const [field, list] of Object.entries(lists)) {
    const m: Record<string, string> = {};
    if (Array.isArray(list)) {
      for (const o of list) {
        const k = o?.id ?? o?.value;
        const v = o?.name ?? o?.label;
        if (k != null && v != null) m[String(k)] = String(v);
      }
    } else if (list && typeof list === "object") {
      for (const [k, v] of Object.entries(list as Record<string, unknown>)) {
        if (v != null && typeof v !== "object") m[k] = String(v);
      }
    }
    maps[field] = m;
  }
  return (field, value) => maps[field]?.[value] ?? value;
}
